import React, { Suspense, useEffect } from "react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import Layout from "./Layouts/Layout"
import { useSelector } from "react-redux"
import { selectIsAuthenticated } from "./redux/auth/selectors"
import TimestampProvider from "./components/TimeDurationFilter/TimeStampProvider"
import { RoutePermissionProvider } from "./context/RoutePermissionContext"
import DynamicComponentRenderer from "./screens/DynamicReactComponent/DynamicComponentRenderer"
import ForgotChangePassword from "./screens/ForgotChangePassword/ForgotChangePassword"
const TranslationManagement = React.lazy(
    () => import("./screens/Translations/TranslationManagement"),
)

const Website = React.lazy(() => import("./screens/Website"))
const Login = React.lazy(() => import("./screens/Login"))
const AuditLogs = React.lazy(() => import("./screens/AuditLogs/AuditLogs"))
const NotFound = React.lazy(() => import("./screens/NotFound"))
const GeneralPageLayout = React.lazy(() => import("./screens/GeneralPageLayout/GeneralPageLayout"))
const Zone = React.lazy(() => import("./screens/Zones/Zone"))
const AssetMapping = React.lazy(() => import("./screens/Zones/AssetMapping"))
const Alerts = React.lazy(() => import("./screens/Alerts/Alerts"))
const ChangePassword = React.lazy(() => import("./screens/ChangePassword"))
const ForgotPassword = React.lazy(() => import("./screens/ForgotPassword/ForgotPassword"))
const AddAsset = React.lazy(() => import("./screens/DynamicAssets/AddAsset"))
const AssetManagement = React.lazy(() => import("./screens/AssetManagement/AssetManagement"))
const AssetExtension = React.lazy(() => import("./screens/AssetExtension/AssetExtension"))
const DynamicAssetWrapper = React.lazy(
    () => import("./screens/DynamicAssetsModule/DynamicAssetWrapper"),
)
const DynamicRoutingHistory = React.lazy(
    () => import("./screens/DynamicRoutingHistory/DynamicRoutingHistory"),
)
const AssetsSubscriptions = React.lazy(() => import("./screens/CRM/AssetsSubscriptions"))
const DataFormat = React.lazy(() => import("./screens/DataFormat/DataFormat"))
const Dashboard = React.lazy(() => import("./screens/Dashboard"))
const DynamicReporting = React.lazy(() => import("./screens/DynamicReporting/DynamicReporting"))
const DynamicPageRenderer = React.lazy(
    () => import("./screens/DynamicReactComponent/DynamicPageRenderer"),
)
const RoleManagement = React.lazy(() => import("./screens/RoleManagement/RoleManagement"))
const UserManagement = React.lazy(() => import("./screens/UserManagement/UserManagement"))

export default function AppRoutes() {
    const isAuthenticated = useSelector(selectIsAuthenticated)

    // The menuitems, sub zones view are removed from localstorage when a user changes a route manually.
    useEffect(() => {
        if (location.pathname && location.pathname == "/") {
            localStorage.removeItem("menuitems")
            localStorage.removeItem("isNodeView") // Subzone viewing parameter
            localStorage.removeItem("zoneId") // Subzone viewing parameter
            localStorage.removeItem("map_app_key") // to remove the dropdown selected in operator module
        }
    }, [])

    return (
        <BrowserRouter>
            <RoutePermissionProvider>
                <Suspense fallback={<div />}>
                    <Routes>
                        {isAuthenticated || localStorage.getItem("token") ? (
                            <>
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/`}
                                    element={<Website />}
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/dashboard`}
                                    element={
                                        <Layout fullView={true}>
                                            <Dashboard />
                                        </Layout>
                                    }
                                />

                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/change-password`}
                                    element={<ChangePassword />}
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/assets-detail`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicAssetWrapper />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/assets-detail/add-update`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicComponentRenderer
                                                componentKey={"dynamic-form-builder"}
                                                componentName={"dynamic-form-builder"}
                                            />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/assets-detail/details-page`}
                                    element={
                                        <Layout fullView={true}>
                                            <TimestampProvider>
                                                <DynamicPageRenderer />
                                            </TimestampProvider>
                                        </Layout>
                                    }
                                />

                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/extension`}
                                    element={
                                        <Layout fullView={true}>
                                            <AssetExtension />
                                        </Layout>
                                    }
                                />

                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/add-asset`}
                                    element={
                                        <Layout fullView={true}>
                                            <AddAsset />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/audit-logs`}
                                    element={
                                        <Layout fullView={true}>
                                            <AuditLogs />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/:page_name`}
                                    element={
                                        <Layout fullView={true}>
                                            <GeneralPageLayout />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/reporting`}
                                    element={
                                        <Layout fullView={true}>
                                            <TimestampProvider>
                                                <DynamicReporting />
                                            </TimestampProvider>
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/routing`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicRoutingHistory />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/subscription`}
                                    element={
                                        <Layout fullView={true}>
                                            <AssetsSubscriptions />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/role-management-permissions`}
                                    element={
                                        <Layout fullView={true}>
                                            <RoleManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/my-visits`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicComponentRenderer
                                                componentKey={"my-visits"}
                                                componentName={"my-visits"}
                                            />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/roster-management`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicComponentRenderer
                                                componentKey={"roster-management"}
                                                componentName={"roster-management"}
                                            />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/patient-schedule`}
                                    element={
                                        <Layout fullView={true}>
                                            <DynamicComponentRenderer
                                                componentKey={"patient-schedule"}
                                                componentName={"patient-schedule"}
                                            />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/translations`}
                                    element={
                                        <Layout fullView={true}>
                                            <TranslationManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/my-visits/details-page`}
                                    element={
                                        <Layout fullView={true}>
                                            <TimestampProvider>
                                                <DynamicComponentRenderer
                                                    componentKey={"my-visits-detail-page"}
                                                    componentName={"my-visits-detail-page"}
                                                />
                                            </TimestampProvider>
                                        </Layout>
                                    }
                                />

                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/user-management`}
                                    element={
                                        <Layout fullView={true}>
                                            <UserManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/zone`}
                                    element={
                                        <Layout fullView={true}>
                                            <Zone />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/asset-mapping`}
                                    element={
                                        <Layout fullView={true}>
                                            <AssetMapping />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/alerts`}
                                    element={
                                        <Layout fullView={true}>
                                            <Alerts />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/asset-management`}
                                    element={
                                        <Layout fullView={true}>
                                            <AssetManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/data-format`}
                                    element={
                                        <Layout fullView={true}>
                                            <DataFormat />
                                        </Layout>
                                    }
                                />
                            </>
                        ) : (
                            <>
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/`}
                                    element={<Login />}
                                />
                                <Route
                                    exacts
                                    path={`${process.env.PUBLIC_URL}/forgot-password`}
                                    element={<ForgotPassword />}
                                />
                                <Route
                                    exact
                                    path={`${process.env.PUBLIC_URL}/forgot-change-password`}
                                    element={<ForgotChangePassword />}
                                />

                                <Route path="*" element={<NotFound />} />
                            </>
                        )}
                    </Routes>
                </Suspense>
            </RoutePermissionProvider>
        </BrowserRouter>
    )
}
